import { BaseComponent } from '../../base-component';
import { HtmlOffersPageLinkReplacer } from './html-offers-page-link-replacer';
import { ArticleOptionsServices } from '../../global';
import { BenefitPrice, BenefitState, LowestOfferBenefitPrices } from '../types';
import { ActiveBenefits } from './active-benefits';
import { EventCallback } from '@otto-ec/global-resources/event-q-bus';

export class CompetingOffersComponent extends BaseComponent {
    public rootSelector = '.js_pdp_competing-offers';

    private readonly FT9_BENEFIT_ACTIVE_EVENT = 'ft9.benefit.active';
    private readonly FT9_BENEFIT_QUERY_ACTIVE_EVENT = 'ft9.benefit.queryActive';
    private readonly ARTICLE_OPTIONS_CHANGED_EVENT = 'ft5.pdp.articleOptionsChanged';
    private readonly ARTICLE_OPTIONS_RECEIVE_SERVICE_INFORMATION_EVENT = 'ft5.articleoptions.receiveServiceInformation';

    private articleOptionsEventListener?: EventCallback<[]> | null;
    private benefitsEventListener?: EventCallback<[]> | null;
    private activeBenefits: ActiveBenefits;
    private htmlOffersPageLinkReplacer: HtmlOffersPageLinkReplacer;

    constructor() {
        super();
        this.htmlOffersPageLinkReplacer = new HtmlOffersPageLinkReplacer(this.rootSelector);
        this.activeBenefits = new ActiveBenefits();
    }

    public async init(): Promise<void> {
        return;
    }

    public async onHtmlLoaded(): Promise<unknown> {
        this.deregisterEventListeners();
        if (!this.isPresent()) {
            return;
        }
        this.htmlOffersPageLinkReplacer.initializeOffersLink();
        this.activeBenefits = new ActiveBenefits();
        this.registerBenefitEventListener();
        this.registerArticleOptionsEventListeners();
        this.emitEventForActiveBenefits();

        return Promise.resolve(undefined);
    }

    private registerBenefitEventListener() {
        this.benefitsEventListener = o_global.eventQBus.on(this.FT9_BENEFIT_ACTIVE_EVENT, (benefitState: BenefitState) => {
            this.changeActiveBenefitState(benefitState);
            this.replacePriceInHtml(this.activeBenefits.lowestActiveBenefitPrice);
        });
    }

    private replacePriceInHtml(lowestActiveBenefitPrice: BenefitPrice | undefined) {
        if (lowestActiveBenefitPrice) {
            this.findPriceElementInHtml().textContent = lowestActiveBenefitPrice.lowestPriceFormatted;
        } else {
            this.findPriceElementInHtml().textContent = this.readInitialPriceFromHtml();
        }
    }

    private changeActiveBenefitState(benefitState: BenefitState) {
        if (!benefitState.active) {
            this.activeBenefits.remove(benefitState.id);
            return;
        }

        const lowestOfferBenefitPrices = this.readLowestOfferBenefitPricesFromHtml();
        const lowestOfferBenefitPrice = lowestOfferBenefitPrices[benefitState.id];
        if (lowestOfferBenefitPrice) {
            this.activeBenefits.add(benefitState.id, lowestOfferBenefitPrice);
        }
    }

    private readInitialPriceFromHtml() {
        const initialPrice = this.findPriceElementInHtml().getAttribute('data-initial-price');
        if (!initialPrice) {
            throw new Error('Could not find initial lowest offer price in html');
        }
        return initialPrice;
    }

    private emitEventForActiveBenefits() {
        const competingVariationIdsWithBenefits = this.readCompetingVariationIdsWithBenefits();
        competingVariationIdsWithBenefits.forEach(variationId => {
            o_global.eventQBus.emit(this.FT9_BENEFIT_QUERY_ACTIVE_EVENT, { variationId });
        });
    }

    private readCompetingVariationIdsWithBenefits() {
        const variationIdsWithBenefits = this.getRootElement().getAttribute('data-competing-variation-ids-with-benefits');
        if (!variationIdsWithBenefits) {
            throw new Error('Failed to read data-competing-variation-ids-with-benefits');
        }
        const parsedVariationIds: string[] = JSON.parse(variationIdsWithBenefits);
        return parsedVariationIds;
    }

    private registerArticleOptionsEventListeners() {
        o_global.eventQBus.emit(this.ARTICLE_OPTIONS_RECEIVE_SERVICE_INFORMATION_EVENT, {
            callback: (articleOptionsServices: ArticleOptionsServices) => {
                if (articleOptionsServices.services.length > 0) {
                    this.htmlOffersPageLinkReplacer.generateAndReplaceOffersPageLink(articleOptionsServices);
                }
            },
        });
        this.articleOptionsEventListener = o_global.eventQBus.on(
            this.ARTICLE_OPTIONS_CHANGED_EVENT,
            (articleOptionsServices: ArticleOptionsServices) => {
                this.htmlOffersPageLinkReplacer.generateAndReplaceOffersPageLink(articleOptionsServices);
            }
        );
    }

    private deregisterEventListeners() {
        if (this.benefitsEventListener) {
            o_global.eventQBus.off(this.FT9_BENEFIT_ACTIVE_EVENT, this.benefitsEventListener);
        }
        if (this.articleOptionsEventListener) {
            o_global.eventQBus.off(this.ARTICLE_OPTIONS_CHANGED_EVENT, this.articleOptionsEventListener);
        }
    }

    private readLowestOfferBenefitPricesFromHtml(): LowestOfferBenefitPrices {
        const lowestOfferBenefitPricesJson = this.getRootElement().getAttribute('data-lowest-offer-benefit-prices-json');
        if (!lowestOfferBenefitPricesJson) {
            throw new Error('Failed to read lowestOfferBenefitPricesJson from html');
        }
        return JSON.parse(lowestOfferBenefitPricesJson);
    }

    private findPriceElementInHtml() {
        const priceElement = document.querySelector('.js_pdp_competing-offers__lowest-retail-price');
        if (!priceElement) {
            throw new Error('Failed to find price element in html');
        }
        return priceElement;
    }
}
